import { ArrowDownTrayIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import ExcelJS from 'exceljs/dist/exceljs.min.js';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import type { AsyncStatus } from '../../../@types/AsyncStatus';
import type { SCIListItem } from '../../../@types/SCI';
import type { RootState } from '../../../store/store';
import { JWTHeader } from '../../../utils/JWTHeader';
import { ROUTES } from '../../../utils/routes';
import Badge from '../../common/Badge';
import MUITable from '../../common/MUITable';
import { showToast } from '../../common/Notification';
import RedirectionMapButtons from '../../common/RedirectionMapButtons/RedirectionMapButtons.module';
import Spinner from '../../common/Spinner';

type Props = {
  selectedSirens: Set<string>;
  onToggleSelection: (siren: string, name: string, checked: boolean) => void;
  onBulkSelectionChange: (items: Array<{ siren: string; name: string }>) => void;
};

function SCIsTransactionTable({ selectedSirens, onToggleSelection, onBulkSelectionChange }: Props) {
  const navigate = useNavigate();
  const jwt = useSelector((state: RootState) => state.auth.token);
  const company = useSelector((state: RootState) => state.auth.company);
  const [status, setStatus] = useState<AsyncStatus>('IDLE');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [rows, setRows] = useState<SCIListItem[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [currentSortField, setCurrentSortField] = useState('');
  const [currentSortDirection, setCurrentSortDirection] = useState<'asc'|'desc'>('asc');
  const [kpis, setKpis] = useState<{ totalScis: number; totalLotsWithin: number; totalLotsTotal: number } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [filters, setFilters] = useState({
    qualified: 'all' as 'all'|'yes'|'no',
    targeted: 'all' as 'all'|'yes'|'no',
    targetedTransaction: 'all' as 'all'|'yes'|'no',
    lotsCopro: 'all' as 'all'|'1'|'>1'|'>5'|'>10',
    lotsTotal: 'all' as 'all'|'1'|'>1'|'>5'|'>10',
  });
  const [isExporting, setIsExporting] = useState(false);
  const [isBulkSelecting, setIsBulkSelecting] = useState(false);

  const filteredRows = useMemo(() => rows, [rows]);
  const isAllSelected = useMemo(
    () => totalCount > 0 && selectedSirens.size >= totalCount,
    [selectedSirens, totalCount],
  );

  const fetchAllRows = useCallback(async(): Promise<SCIListItem[]> => {
    if (!company?.id || !jwt) { return []; }

    const params = new URLSearchParams();
    params.set('limit', '1000');
    params.set('step', '1');
    if (currentSortField) {
      params.set('sortField', currentSortField);
      params.set('sortDirection', currentSortDirection);
    }
    if (filters.qualified && filters.qualified !== 'all') { params.set('filterQualified', filters.qualified); }
    if (filters.targeted && filters.targeted !== 'all') { params.set('filterTargetedGestion', filters.targeted); }
    if (filters.targetedTransaction && filters.targetedTransaction !== 'all') { params.set('filterTargetedTransaction', filters.targetedTransaction); }
    if (filters.lotsCopro && filters.lotsCopro !== 'all') { params.set('filterLotsCopro', filters.lotsCopro); }
    if (filters.lotsTotal && filters.lotsTotal !== 'all') { params.set('filterLotsTotal', filters.lotsTotal); }
    const trimmedSearch = appliedSearch.trim();
    if (trimmedSearch) {
      params.set('filterSearch', trimmedSearch);
    }

    const baseUrl = `/api/companies/${company.id}/scis`;

    const firstResp = await fetch(`${baseUrl}?${params.toString()}`, JWTHeader(jwt));
    if (!firstResp.ok) { throw new Error(`${firstResp.status}`); }
    const firstData = await firstResp.json();
    let allRows: SCIListItem[] = firstData.results || [];
    const pagination = firstData.pagination || { totalResults: allRows.length, itemsPerPage: allRows.length, currentPage: 1, totalPages: 1 };
    const totalPages = pagination.totalPages || Math.ceil((pagination.totalResults || allRows.length) / (pagination.itemsPerPage || 1000));

    if (totalPages > 1) {
      const fetches: Promise<Response>[] = [];
      for (let step = 2; step <= totalPages; step += 1) {
        const pageParams = new URLSearchParams(params);
        pageParams.set('step', String(step));
        fetches.push(fetch(`${baseUrl}?${pageParams.toString()}`, JWTHeader(jwt)));
      }
      const responses = await Promise.all(fetches);
      const jsons = await Promise.all(responses.map(async(r) => (r.ok ? r.json() : { results: [] })));
      jsons.forEach((data) => {
        if (Array.isArray(data.results)) {
          allRows = [...allRows, ...(data.results as SCIListItem[])];
        }
      });
    }

    return allRows;
  }, [appliedSearch, company?.id, currentSortDirection, currentSortField, filters.lotsCopro, filters.lotsTotal, filters.qualified, filters.targeted, filters.targetedTransaction, jwt]);

  const fetchRows = async(
    nextPage = page,
    nextRowsPerPage = rowsPerPage,
    nextSortField = currentSortField,
    nextSortDirection: 'asc'|'desc' = currentSortDirection,
    nextSearch = appliedSearch,
  ) => {
    if (!company?.id || !jwt) {return;}
    setStatus('PENDING');
    try {
      const params = new URLSearchParams();
      params.set('limit', String(nextRowsPerPage));
      params.set('step', String(nextPage + 1));
      if (nextSortField) {
        params.set('sortField', nextSortField);
        params.set('sortDirection', nextSortDirection);
      }
      if (filters.qualified && filters.qualified !== 'all') {params.set('filterQualified', filters.qualified);}    
      if (filters.targeted && filters.targeted !== 'all') {params.set('filterTargetedGestion', filters.targeted);}  
      if (filters.targetedTransaction && filters.targetedTransaction !== 'all') {params.set('filterTargetedTransaction', filters.targetedTransaction);} 
      if (filters.lotsCopro && filters.lotsCopro !== 'all') {params.set('filterLotsCopro', filters.lotsCopro);}   
      if (filters.lotsTotal && filters.lotsTotal !== 'all') {params.set('filterLotsTotal', filters.lotsTotal);}     
      const trimmedSearch = nextSearch.trim();
      if (trimmedSearch) {
        params.set('filterSearch', trimmedSearch);
      }
      const resp = await fetch(`/api/companies/${company.id}/scis?${params.toString()}`, JWTHeader(jwt));
      if (!resp.ok) {throw new Error(`${resp.status}`);}    
      const data = await resp.json();
      const results: SCIListItem[] = data.results || [];
      const pagination = data.pagination || { totalResults: results.length, itemsPerPage: nextRowsPerPage, currentPage: nextPage + 1 };
      setRows(results);
      setTotalCount(pagination.totalResults || results.length);
      setPage(Math.max(0, (pagination.currentPage ?? (nextPage + 1)) - 1));
      setRowsPerPage(pagination.itemsPerPage ?? nextRowsPerPage);
      setStatus('SUCCESS');
    } catch (e) {
      console.error(e);
      setErrorMessage('Erreur lors du chargement des SCIs');
      setStatus('ERROR');
      showToast('error', 'Une erreur est survenue lors du chargement des données.');
    }
  };

  const fetchKpis = async(nextSearch = appliedSearch) => {
    if (!company?.id || !jwt) {return;}
    try {
      const params = new URLSearchParams();
      if (filters.qualified && filters.qualified !== 'all') {params.set('filterQualified', filters.qualified);}    
      if (filters.targeted && filters.targeted !== 'all') {params.set('filterTargetedGestion', filters.targeted);}  
      if (filters.targetedTransaction && filters.targetedTransaction !== 'all') {params.set('filterTargetedTransaction', filters.targetedTransaction);} 
      if (filters.lotsCopro && filters.lotsCopro !== 'all') {params.set('filterLotsCopro', filters.lotsCopro);}   
      if (filters.lotsTotal && filters.lotsTotal !== 'all') {params.set('filterLotsTotal', filters.lotsTotal);}     
      const trimmedSearch = nextSearch.trim();
      if (trimmedSearch) {
        params.set('filterSearch', trimmedSearch);
      }
      const r = await fetch(`/api/companies/${company.id}/scis/kpis?${params.toString()}`, JWTHeader(jwt));
      if (!r.ok) { return; }
      const data = await r.json();
      setKpis({
        totalScis: Number(data.totalScis ?? 0),
        totalLotsWithin: Number(data.totalLotsWithin ?? 0),
        totalLotsTotal: Number(data.totalLotsTotal ?? 0),
      });
    } catch {
      // silent
    }
  };

  useEffect(() => {
    if (!company?.id || !jwt) {return;}
    fetchRows(0, rowsPerPage, currentSortField, currentSortDirection, appliedSearch);
    fetchKpis(appliedSearch);
  }, [company?.id, jwt]);

  useEffect(() => {
    fetchKpis(appliedSearch);
    fetchRows(0, rowsPerPage, currentSortField, currentSortDirection, appliedSearch);
  }, [filters, appliedSearch]);

  const handlePageChange = (nextPage: number, nextRowsPerPage: number) => {
    setPage(nextPage);
    setRowsPerPage(nextRowsPerPage);
    fetchRows(nextPage, nextRowsPerPage, currentSortField, currentSortDirection, appliedSearch);
  };

  const handleSortChange = (field: string, direction: 'asc'|'desc') => {
    setCurrentSortField(field);
    setCurrentSortDirection(direction);
    fetchRows(0, rowsPerPage, field, direction, appliedSearch);
  };

  const handleResetFilters = () => {
    setFilters({
      qualified: 'all',
      targeted: 'all',
      targetedTransaction: 'all',
      lotsCopro: 'all',
      lotsTotal: 'all',
    });
    setPage(0);
    setSearchTerm('');
    setAppliedSearch('');
  };

  const handleHeaderSort = (field: string) => {
    const nextDir: 'asc'|'desc' = (currentSortField === field && currentSortDirection === 'asc') ? 'desc' : 'asc';
    handleSortChange(field, nextDir);
  };

  const handleSearchSubmit = (event?: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>) => {
    if (event) { event.preventDefault(); }
    const trimmed = searchTerm.trim();
    setSearchTerm(trimmed);
    setPage(0);
    if (trimmed === appliedSearch.trim()) {
      fetchRows(0, rowsPerPage, currentSortField, currentSortDirection, trimmed);
      fetchKpis(trimmed);
      return;
    }
    setAppliedSearch(trimmed);
  };

  const getDisplayName = useCallback((sci: SCIListItem): string => {
    const isDefined = (v?: string | null) => !!v && v !== '[ND]';
    if (isDefined(sci.denominationUniteLegale)) { return sci.denominationUniteLegale as string; }
    if (isDefined(sci.prenom1UniteLegale) && isDefined(sci.nomUniteLegale)) { return `${sci.prenom1UniteLegale as string} ${sci.nomUniteLegale as string}`; }
    if (isDefined(sci.denominationUsuelleEtablissement)) { return sci.denominationUsuelleEtablissement as string; }
    if (isDefined(sci.name)) { return sci.name as string; }
    return `SCI ${sci.siren}`;
  }, []);

  const columnsData = filteredRows.map((sci) => {
    const primaryProp = sci.properties?.find((p) => p && p.copro);
    const copro = sci.primaryCopro || primaryProp?.copro;
    const seatAddress = `${sci.numeroVoieEtablissement || ''} ${sci.typeVoieEtablissement || ''} ${sci.libelleVoieEtablissement || ''}, ${sci.codePostalEtablissement || ''} ${sci.libelleCommuneEtablissement || ''}`;
    const name = getDisplayName(sci);
    const isSelected = selectedSirens.has(sci.siren);
    return ({
      VALIDE: isSelected,
      'Nom immeuble': (
        <div className="flex flex-col items-start">
          <div className="flex items-center">
            <RedirectionMapButtons address={`${copro?.address || seatAddress}`} />
            <span className="ml-1 font-semibold">{copro?.name || 'N/A'}</span>
          </div>
          {copro?.remoteId && (
            <div className="mt-1 ml-7 text-xs text-gray-600">
              Immatriculation : {copro.remoteId}
            </div>
          )}
          {copro?.id && (
            <button
              className="mt-1 ml-7 flex items-center text-xs text-rgpd-blue hover:underline"
              onClick={() => navigate(ROUTES.ANALYZING_COPRO_MANAGING_ID.replace(':coproId', copro.id!.toString()))}
            >
              <ArrowTopRightOnSquareIcon className="mr-1 h-3 w-3" /> Voir la fiche copro
            </button>
          )}
        </div>
      ),
      'Adresse immeuble': (
        <div className="text-xs text-gray-600">
          {copro?.address && (
            <>
              {copro.address}
              {copro.cp ? `, ${copro.cp}` : ''}
              {copro.city ? ` ${copro.city}` : ''}
            </>
          )}
        </div>
      ),
      'Nom SCI': (<span className="font-semibold">{name}</span>),
      'Adresse du siège de la SCI': (<div className="text-xs text-gray-600">{seatAddress.trim()}</div>),
      'Lots dans la copro': sci.stats.propertiesWithinCopro,
      'Total lots SCI': sci.stats.totalProperties,
      'Contact qualifié': (
        <span className={`rounded px-2 py-1 text-xs ${sci.qualification?.contactQualified ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
          {sci.qualification?.contactQualified ? 'Oui' : 'Non'}
        </span>
      ),
      'Cible Gest Loc': (
        <span className={`rounded px-2 py-1 text-xs ${sci.qualification?.targetedGestion ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
          {sci.qualification?.targetedGestion ? 'Oui' : 'Non'}
        </span>
      ),
      'Cible Transac': (
        <span className={`rounded px-2 py-1 text-xs ${sci.qualification?.targetedTransaction ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
          {sci.qualification?.targetedTransaction ? 'Oui' : 'Non'}
        </span>
      ),
      ___siren: sci.siren,
      ___name: name,
    });
  });

  const headersForTable = useMemo(() => [
    { title: 'Nom immeuble' },
    { title: 'Adresse immeuble' },
    { title: 'Nom SCI', sortable: true, sortField: 'denominationUniteLegale', onSort: handleHeaderSort },
    { title: 'Adresse du siège de la SCI' },
    { title: 'Lots dans la copro', sortable: true, sortField: 'stats.propertiesWithinCopro', onSort: handleHeaderSort },
    { title: 'Total lots SCI', sortable: true, sortField: 'stats.totalProperties', onSort: handleHeaderSort },
    { title: 'Contact qualifié', sortable: true, sortField: 'qualification.contactQualified', onSort: handleHeaderSort },
    { title: 'Cible Gest Loc', sortable: true, sortField: 'qualification.targetedGestion', onSort: handleHeaderSort },
    { title: 'Cible Transac', sortable: true, sortField: 'qualification.targetedTransaction', onSort: handleHeaderSort },
  ], [handleHeaderSort]);

  const exportToExcel = useCallback(async() => {
    if (!company?.id || !jwt) {return;}
    if (selectedSirens.size === 0) {
      showToast('warning', "Veuillez sélectionner au moins une SCI avant d'exporter.");
      return;
    }

    setIsExporting(true);
    try {
      const allRows = await fetchAllRows();
      if (allRows.length === 0) {
        showToast('warning', 'Aucune SCI correspondante à exporter.');
        return;
      }

      const rowsToExport = allRows.filter((row) => selectedSirens.has(row.siren));
      if (rowsToExport.length === 0) {
        showToast('warning', 'Aucune SCI sélectionnée à exporter.');
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('SCIs');
      worksheet.addRow([
        'Identifiant RNIC',
        'Nom immeuble',
        'Adresse immeuble',
        'Dénomination SCI',
        'SIREN de la SCI',
        'Adresse du siège de la SCI',
        'Civilité contact',
        'Nom contact',
        'Prénom contact',
        'Téléphone contact',
        'Email contact',
        'Adresse contact',
        'Lots dans la copro',
        'Total lots SCI',
        'Contact qualifié',
        'Cible Gest Loc',
        'Cible Transac',
      ]);

      rowsToExport.forEach((row) => {
        const primaryProp = row.properties?.find((p) => p && p.copro);
        const copro = row.primaryCopro || primaryProp?.copro;
        const coproName = copro?.name || '';
        const coproAddress = copro?.address || '';
        const rnic = copro?.remoteId || '';
        const denomination = getDisplayName(row);
        const siegeAddress = [
          row.numeroVoieEtablissement || '',
          row.indiceRepetitionEtablissement || '',
          row.typeVoieEtablissement || '',
          row.libelleVoieEtablissement || '',
        ]
          .filter((part) => !!part && String(part).trim() !== '')
          .join(' ')
          .replaceAll(/\s+/g, ' ')
          .trim();
        const contact = row.qualification?.contact || {};
        worksheet.addRow([
          rnic,
          coproName,
          coproAddress,
          denomination,
          row.siren,
          siegeAddress,
          contact?.civilite || '',
          contact?.nom || '',
          contact?.prenom || '',
          contact?.telephone || '',
          contact?.email || '',
          contact?.adresse || '',
          row.stats.propertiesWithinCopro,
          row.stats.totalProperties,
          row.qualification?.contactQualified ? 'Oui' : 'Non',
          row.qualification?.targetedGestion ? 'Oui' : 'Non',
          row.qualification?.targetedTransaction ? 'Oui' : 'Non',
        ]);
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `SCIs_transaction_${new Date().toISOString()}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(link.href);
      link.remove();
    } catch (error) {
      console.error(error);
      showToast('error', "Une erreur est survenue lors de l'export.");
    } finally {
      setIsExporting(false);
    }
  }, [company?.id, fetchAllRows, getDisplayName, jwt, selectedSirens]);

  const handleToggleSelectAll = useCallback(async() => {
    if (!company?.id || !jwt) { return; }
    if (isBulkSelecting) { return; }

    if (isAllSelected) {
      onBulkSelectionChange([]);
      return;
    }

    setIsBulkSelecting(true);
    try {
      const allRows = await fetchAllRows();
      if (allRows.length === 0) {
        showToast('warning', 'Aucune SCI disponible à sélectionner.');
        return;
      }

      const uniqueItems = new Map<string, string>();
      allRows.forEach((row) => {
        if (row.siren) {
          uniqueItems.set(row.siren, getDisplayName(row));
        }
      });

      onBulkSelectionChange(
        [...uniqueItems.entries()].map(([siren, name]) => ({ siren, name })),
      );
    } catch (error) {
      console.error(error);
      showToast('error', 'Impossible de sélectionner toutes les SCI.');
    } finally {
      setIsBulkSelecting(false);
    }
  }, [company?.id, fetchAllRows, getDisplayName, isAllSelected, isBulkSelecting, jwt, onBulkSelectionChange]);

  // headers are defined in headersForTable above; no duplicate definition needed

  return (
    <div>
      <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3">
        <div className="rounded border bg-white p-3">
          <div className="text-center text-2xl font-bold">{kpis ? kpis.totalScis.toLocaleString() : '-'}</div>
          <div className="text-center text-xs text-gray-500 uppercase">SCI présentes dans vos copropriétés</div>
        </div>
        <div className="rounded border bg-white p-3">
          <div className="text-center text-2xl font-bold">{kpis ? kpis.totalLotsWithin.toLocaleString() : '-'}</div>
          <div className="text-center text-xs text-gray-500 uppercase">Nombre de lots gérés dans vos copropriétés</div>
        </div>
        <div className="rounded border bg-white p-3">
          <div className="text-center text-2xl font-bold">{kpis ? kpis.totalLotsTotal.toLocaleString() : '-'}</div>
          <div className="text-center text-xs text-gray-500 uppercase">Nombre total de lots gérés par ces SCI</div>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <form className="flex flex-wrap items-center gap-2 text-sm" onSubmit={handleSearchSubmit}>
          <div className="flex flex-col">
            <label className="text-xs text-gray-600">Statut contact qualifié</label>
            <select
              className="rounded border px-2 py-1"
              value={filters.qualified}
              onChange={(e) => { const v = e.target.value as typeof filters.qualified; setFilters((f) => ({ ...f, qualified: v })); setPage(0); }}
            >
              <option value="all">Tous</option>
              <option value="yes">Qualifiés</option>
              <option value="no">Non qualifiés</option>
            </select>
          </div>
          <div className="flex flex-col">
            <label className="text-xs text-gray-600">Statut ciblé Gest Loc</label>
            <select
              className="rounded border px-2 py-1"
              value={filters.targeted}
              onChange={(e) => { const v = e.target.value as typeof filters.targeted; setFilters((f) => ({ ...f, targeted: v })); setPage(0); }}
            >
              <option value="all">Tous</option>
              <option value="yes">Ciblés</option>
              <option value="no">Non ciblés</option>
            </select>
          </div>
          <div className="flex flex-col">
            <label className="text-xs text-gray-600">Statut ciblé Transac</label>
            <select
              className="rounded border px-2 py-1"
              value={filters.targetedTransaction}
              onChange={(e) => { const v = e.target.value as typeof filters.targetedTransaction; setFilters((f) => ({ ...f, targetedTransaction: v })); setPage(0); }}
            >
              <option value="all">Tous</option>
              <option value="yes">Ciblés</option>
              <option value="no">Non ciblés</option>
            </select>
          </div>
          <div className="flex flex-col">
            <label className="text-xs text-gray-600">Lots dans copro</label>
            <select
              className="rounded border px-2 py-1"
              value={filters.lotsCopro}
              onChange={(e) => { const v = e.target.value as typeof filters.lotsCopro; setFilters((f) => ({ ...f, lotsCopro: v })); setPage(0); }}
            >
              <option value="all">Tous</option>
              <option value="1">1 lot</option>
              <option value=">1">Plus de 1 lot</option>
              <option value=">5">Plus de 5 lots</option>
              <option value=">10">Plus de 10 lots</option>
            </select>
          </div>
          <div className="flex flex-col">
            <label className="text-xs text-gray-600">Total lots SCI</label>
            <select
              className="rounded border px-2 py-1"
              value={filters.lotsTotal}
              onChange={(e) => { const v = e.target.value as typeof filters.lotsTotal; setFilters((f) => ({ ...f, lotsTotal: v })); setPage(0); }}
            >
              <option value="all">Tous</option>
              <option value="1">1 lot</option>
              <option value=">1">Plus de 1 lot</option>
              <option value=">5">Plus de 5 lots</option>
              <option value=">10">Plus de 10 lots</option>
            </select>
          </div>
          <div className="flex flex-col">
            <label className="text-xs text-gray-600" htmlFor="sci-overall-search">Recherche</label>
            <div className="flex items-center gap-2">
              <input
                id="sci-overall-search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                className="min-w-[260px] rounded border px-2 py-1"
                placeholder="Nom/Adresse/Immatriculation immeuble ou SCI"
                type="search"
              />
              <button
                type="submit"
                className="rounded border border-rgpd-blue bg-rgpd-blue px-3 py-2 text-white hover:bg-rgpd-blue-dark"
              >
                Rechercher
              </button>
            </div>
          </div>
        </form>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleResetFilters}
            className="flex items-center rounded border border-gray-400 px-3 py-2 text-gray-700 hover:bg-gray-100"
          >
            Réinitialiser
          </button>
          <button
            type="button"
            onClick={handleToggleSelectAll}
            disabled={status !== 'SUCCESS' || totalCount === 0 || isBulkSelecting}
            className="flex items-center rounded border border-rgpd-blue px-3 py-2 text-rgpd-blue hover:bg-rgpd-blue hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isBulkSelecting ? 'Traitement…' : isAllSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
          </button>
          <button
            type="button"
            onClick={exportToExcel}
            disabled={isExporting}
            className="flex items-center rounded border border-rgpd-green px-3 py-2 text-rgpd-green hover:bg-rgpd-green hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
          >
            <ArrowDownTrayIcon className="mr-2 h-5 w-5" /> {isExporting ? 'Export en cours…' : 'Export'}
          </button>
        </div>
      </div>

      {status === 'PENDING' && (
        <p className="mx-10 my-8 text-center">
          <Spinner aria-label="Chargement" size="lg" />
        </p>
      )}
      {status === 'ERROR' && (
        <div className="my-10 text-center">
          <Badge mode="error" text={errorMessage || 'Erreur inconnue'} />
        </div>
      )}
      {status === 'SUCCESS' && (
        <MUITable
          data={columnsData}
          headers={headersForTable}
          tableClass="min-w-full bg-white border text-sm"
          isSearchable={false}
          disablePagination={false}
          apiPagination
          totalCount={totalCount}
          onPageChange={handlePageChange}
          currentPage={page}
          currentRowsPerPage={rowsPerPage}
          currentSortField={currentSortField}
          dataChangeCallback={(checked: boolean, index: number) => {
            const item = columnsData[index];
            if (!item) { return; }
            const siren = item.___siren as string;
            const name = item.___name as string;
            onToggleSelection(siren, name, checked);
          }}
        />
      )}
    </div>
  );
}

export default SCIsTransactionTable;


